import { useEffect, useMemo, useState } from "react";
import {
  AbsoluteFill,
  Easing,
  Img,
  staticFile,
  useVideoConfig,
  useCurrentFrame,
  spring,
  interpolate,
  delayRender,
  continueRender,
} from "remotion";
import { SAFE_TOP, SAFE_BOTTOM, SAFE_LEFT, SAFE_RIGHT } from "../../lib/types";
import { DEFAULT_EDITION, getEdition } from "../../lib/editions";

const FONTS_TO_LOAD = [
  { family: "Gilroy", url: staticFile("/fonts/Gilroy-Regular.ttf"), weight: "400" },
  { family: "Gilroy", url: staticFile("/fonts/Gilroy-SemiBold.ttf"), weight: "600" },
  { family: "Gilroy", url: staticFile("/fonts/Gilroy-Bold.ttf"), weight: "700" },
  { family: "Gilroy", url: staticFile("/fonts/Gilroy-ExtraBold.ttf"), weight: "800" },
];

// Load each Gilroy weight exactly once for the whole document. The promise is
// memoised at module scope so repeated renders (and re-mounts) reuse the same
// in-flight or settled load instead of re-fetching every font.
let gilroyFontsPromise: Promise<void> | null = null;
function loadGilroyFontsOnce(): Promise<void> {
  if (!gilroyFontsPromise) {
    gilroyFontsPromise = Promise.all(
      FONTS_TO_LOAD.map(async (font) => {
        const loaded = new FontFace(font.family, `url('${font.url}')`, { weight: font.weight });
        await loaded.load();
        document.fonts.add(loaded);
      }),
    ).then(() => undefined);
  }
  return gilroyFontsPromise;
}

const TOP_BAR_H = 150;
const BOTTOM_BAR_H = 125;

const makeShadow = (size: number, color: string) =>
  Array.from({ length: size }, (_, i) => `${color} 0px 0px ${i}px`).join(", ");

export type AnimatedTDCEuFuiCardProps = {
  photoUrl: string;
  photoOffsetX?: number;
  photoOffsetY?: number;
  photoScale?: number;
  editionSlug?: string;
  cityLabel?: string;
  editionDates?: string;
  editionNameLine1?: string;
  editionNameLine2?: string;
  showSafeArea?: boolean;
  /**
   * Frame (inside this card's Sequence) at which the global exit fade
   * should start. The card fades to 0 opacity over the next
   * ELEMENTS_EXIT_DURATION frames. Default = Infinity (no exit).
   */
  exitStartFrame?: number;
};

export const AnimatedTDCEuFuiCard: React.FC<AnimatedTDCEuFuiCardProps> = ({
  photoUrl,
  photoOffsetX = 0,
  photoOffsetY = 0,
  photoScale = 1,
  editionSlug,
  cityLabel,
  editionDates,
  editionNameLine1 = "TDC Floripa 2026",
  editionNameLine2 = "#EuFui",
  showSafeArea,
  exitStartFrame = Number.POSITIVE_INFINITY,
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  const edition = getEdition(editionSlug ?? DEFAULT_EDITION);
  const resolvedCity = cityLabel ?? edition.city;
  const resolvedEditionDates = editionDates ?? edition.editionDates;

  // The delayRender handle is created once via the useState initialiser rather
  // than on every render. Previously `delayRender()` ran in the render body, so
  // each frame of Player playback produced a fresh handle, re-ran the effect,
  // and reloaded all four Gilroy fonts — an endless reload loop. The load
  // itself is memoised at module scope (loadGilroyFontsOnce).
  const [waitForFont] = useState(() => delayRender("load-gilroy-fonts"));
  useEffect(() => {
    loadGilroyFontsOnce().then(() => continueRender(waitForFont));
  }, [waitForFont]);

  const photoSrc = photoUrl || staticFile("/user-placeholder.png");

  // The top and bottom black bars now live inside the safe area so they're
  // visible on platforms that cover the frame edges (TikTok, Reels,
  // Shorts). Photo + text fit between the bars.
  const CONTENT_TOP_INSET = SAFE_TOP + TOP_BAR_H + 30;
  const CONTENT_BOTTOM_INSET = SAFE_BOTTOM + BOTTOM_BAR_H + 40;
  // Photo's bottom edge is flush with the top of the bottom black bar —
  // no gap, so the speaker looks "snapped" onto the bar.
  const PHOTO_BOTTOM_INSET = SAFE_BOTTOM + BOTTOM_BAR_H;

  // ── Responsive text sizing ─────────────────────────────────────────────
  // The photo is rendered inside a fixed-dimension box so long-aspect
  // portrait shots or square placeholders both behave consistently. The
  // photo sits centered on the composition, layered on top of the hero
  // text via DOM order.
  const PHOTO_WIDTH_RATIO = 0.52;
  const PHOTO_HEIGHT_RATIO = 0.42;
  const photoWidth = useMemo(() => width * PHOTO_WIDTH_RATIO * photoScale, [width, photoScale]);
  const photoHeight = useMemo(() => height * PHOTO_HEIGHT_RATIO * photoScale, [height, photoScale]);

  // Headline font size — we have two lines of copy that need to fit inside
  // the content area. Pick the size based on the longest of the two lines
  // so both share a consistent scale.
  const headlineFontSize = useMemo(() => {
    const longest = Math.max(editionNameLine1?.length ?? 0, editionNameLine2?.length ?? 0);
    if (longest <= 8) return 120;
    if (longest <= 12) return 100;
    if (longest <= 16) return 84;
    if (longest <= 22) return 70;
    return 60;
  }, [editionNameLine1, editionNameLine2]);

  // Full bar heights (bars extend from the frame edge to the bottom of
  // the visible portion inside the safe area).
  const TOP_BAR_FULL_H = SAFE_TOP + TOP_BAR_H;
  const BOTTOM_BAR_FULL_H = SAFE_BOTTOM + BOTTOM_BAR_H;

  // ── Animations ─────────────────────────────────────────────────────────
  // Bars fade in quickly; their text contents then cascade in individually
  // with a tiny translate-up + opacity fade. Keeping the bar opacity
  // separate from text opacity keeps the chrome feeling solid while the
  // labels elegantly pop in. Max opacity stays at 0.95 so the background
  // video reads through the bars slightly.
  const barOpacity = interpolate(frame, [0, 10], [0, 0.95], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  // The bar still does a subtle slide (≈ 24 px, much smaller than before).
  const topBarY = interpolate(frame, [0, 15], [-24, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const bottomBarY = interpolate(frame, [0, 15], [24, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Staggered per-label entry helper (opacity + 10 px translateY).
  const labelEntry = (start: number) => {
    const opacity = interpolate(frame, [start, start + 14], [0, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    });
    const ty = interpolate(frame, [start, start + 14], [10, 0], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    });
    return { opacity, transform: `translateY(${ty}px)` };
  };

  const logoEntry = labelEntry(4);
  const dateEntry = labelEntry(8);
  const presencialEntry = labelEntry(10);
  const onlineEntry = labelEntry(14);
  const siteEntry = labelEntry(18);

  const talkTitleOpacity = interpolate(frame, [50, 80], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const talkTitleY = interpolate(frame, [50, 80], [40, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const photoY = spring({ frame: frame - 15, fps, config: { damping: 14, stiffness: 90 } });
  // The photo must always rise in from fully off-screen below the frame,
  // regardless of where the user has dragged it via photoOffsetY. The outer
  // wrapper already applies photoOffsetY as a static translate, so we
  // cancel it out here in the starting value. Since the photo box is anchored
  // above the bottom safe-area bar, the initial offset must also include that
  // inset; otherwise the avatar peeks into frame before the black bar appears.
  const photoTranslateY = interpolate(photoY, [0, 1], [
    PHOTO_BOTTOM_INSET + photoHeight + 40 - photoOffsetY,
    0,
  ]);

  const photoZoom = interpolate(frame, [45, 180], [1.0, 1.04], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // ── Choreographed exit ──────────────────────────────────────────────
  // Each element gets its own slightly-staggered exit animation, all
  // easing "in" (accelerating away) so the card feels like it's physically
  // leaving the screen instead of uniformly dissolving.
  //
  //   Bars            slide off-screen top/bottom
  //   Role badge      fade + scale-down + lift
  //   Talk title      fade + lift
  //   Speaker photo   slide right + fade
  //   Name badge      fade + scale-down
  //   Speaker title   fade + lift
  //
  // A small shared helper keeps the interpolate calls readable.
  const easeIn = Easing.in(Easing.cubic);
  const exitLerp = (startOffset: number, duration: number, from: number, to: number) =>
    interpolate(
      frame,
      [exitStartFrame + startOffset, exitStartFrame + startOffset + duration],
      [from, to],
      { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: easeIn },
    );

  // Top + bottom bars — slide all the way off-screen.
  const topBarExitY = exitLerp(0, 28, 0, -(SAFE_TOP + TOP_BAR_H));
  const bottomBarExitY = exitLerp(0, 28, 0, SAFE_BOTTOM + BOTTOM_BAR_H);

  // Talk title — short lag behind the badge.
  const talkTitleExitOpacity = exitLerp(5, 22, 1, 0);
  const talkTitleExitYOut = exitLerp(5, 22, 0, -22);

  // Photo — sinks off the bottom edge while fading.
  const photoExitY = exitLerp(4, 30, 0, photoHeight + 80);
  const photoExitOpacity = exitLerp(4, 30, 1, 0);

  return (
    <AbsoluteFill
      style={{
        fontFamily: "Gilroy",
        color: "white",
      }}
    >
      {/* The background is now provided by the enclosing TDCPromoVideo
          (grayscale video + format-color duotone tint), so the card
          itself only renders its chrome (bars, content, photo) on top. */}

      {/* ── Top black bar — extends from frame top edge down to the bottom
            of its visible (safe-area) portion ── */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: TOP_BAR_FULL_H,
          backgroundColor: "black",
          opacity: barOpacity,
          transform: `translateY(${topBarY + topBarExitY}px)`,
        }}
      >
        {/* Inner content wrapper — sits in the bar's visible portion, i.e.
            the bottom TOP_BAR_H pixels (aligned to the safe-area top). */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: TOP_BAR_H,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 40px",
          }}
        >
          <Img
            src={staticFile("/edition-logo.png")}
            style={{
              height: 140,
              filter: "drop-shadow(0 0 8px rgba(0,0,0,0.8))",
              ...logoEntry,
            }}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              ...dateEntry,
            }}
          >
            {resolvedEditionDates && (
              <span style={{ fontWeight: "800", fontSize: 78 }}>{resolvedEditionDates}</span>
            )}
            <span style={{ fontWeight: "700", fontSize: 28, marginTop: -14 }}>#euFuiAoTDC</span>
          </div>
        </div>
      </div>

      {/* ── Content area ── */}
      <div
        style={{
          position: "absolute",
          top: CONTENT_TOP_INSET,
          left: 0,
          right: 0,
          bottom: CONTENT_BOTTOM_INSET,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        {/* Hero: two-line headline, sits in the upper portion of the
            content area so the photo (rendered after, with absolute
            positioning near the bottom) layers on top of it. */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            paddingTop: 40,
          }}
        >
          {/* Two-line static headline — replaces the talk title */}
          <div
            style={{
              fontWeight: "800",
              maxWidth: "86%",
              fontSize: headlineFontSize,
              lineHeight: `${headlineFontSize + 4}px`,
              textAlign: "center",
              textShadow: makeShadow(10, "rgba(0,0,0,0.8)"),
              opacity: talkTitleOpacity * talkTitleExitOpacity,
              transform: `translateY(${talkTitleY + talkTitleExitYOut}px)`,
            }}
          >
            <div>{editionNameLine1}</div>
            <div>{editionNameLine2}</div>
          </div>
        </div>
      </div>

      {/* ── Speaker photo — right side, bottom edge inside safe area ── */}
      {/* Outer wrapper applies the user's repositioning offset (set from
          the form) in composition-space pixels. The inner box still owns
          the intro slide-in, Ken Burns zoom and exit animations, so user
          repositioning composes cleanly with the timeline motion. */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          bottom: PHOTO_BOTTOM_INSET,
          width: photoWidth,
          height: photoHeight,
          transform: `translate(calc(-50% + ${photoOffsetX}px), ${photoOffsetY}px)`,
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            filter: "drop-shadow(0 8px 20px rgba(0,0,0,0.5))",
            opacity: photoExitOpacity,
            transform: `translateY(${photoTranslateY + photoExitY}px) scale(${photoZoom})`,
            transformOrigin: "bottom center",
          }}
        >
          <Img
            src={photoSrc}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              objectPosition: "bottom center",
            }}
          />
        </div>
      </div>

      {/* ── Bottom black bar — extends from the top of its visible portion
            down to the frame bottom edge ── */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: BOTTOM_BAR_FULL_H,
          backgroundColor: "black",
          opacity: barOpacity,
          transform: `translateY(${bottomBarY + bottomBarExitY}px)`,
        }}
      >
        {/* Inner content wrapper — sits in the visible portion, i.e. the
            top BOTTOM_BAR_H pixels. */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: BOTTOM_BAR_H,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 40px",
          }}
        >
          {/* Presencial group (icon + label) */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, ...presencialEntry }}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ width: 48, height: 48, flexShrink: 0 }}
            >
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ fontSize: 38, fontWeight: "700" }}>Presencial</span>
              <span style={{ fontSize: 30, fontWeight: "400", opacity: 0.9 }}>{resolvedCity}</span>
            </div>
          </div>

          {/* On-line group (icon + label) — separate stagger */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, ...onlineEntry }}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ width: 48, height: 48, flexShrink: 0 }}
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ fontSize: 38, fontWeight: "700" }}>On-line</span>
              <span style={{ fontSize: 30, fontWeight: "400", opacity: 0.9 }}>Web e Mobile</span>
            </div>
          </div>

          <span
            style={{
              fontWeight: "800",
              fontSize: 52,
              color: "#e8a800",
              ...siteEntry,
            }}
          >
            thedevconf.com
          </span>
        </div>
      </div>

      {/* ── Safe-area overlay (preview only) ─────────────────────────── */}
      {showSafeArea && <SafeAreaOverlay width={width} height={height} />}
    </AbsoluteFill>
  );
};

// ───────────────────────────────────────────────────────────────────────
// Preview-only skeleton. Sketches the social-media UI that will sit in
// the danger zones so the author understands *why* the safe area
// exists: profile chip + mute button at the top, action rail (heart,
// comment, share, bookmark, audio) on the right, @handle + caption +
// hashtags + audio track + playback scrubber + bottom nav along the
// bottom. Everything is drawn with low-opacity white strokes / fills
// so the composition underneath stays readable.
//
// MUST be gated behind showSafeArea so the exported MP4 stays clean.
// ───────────────────────────────────────────────────────────────────────

// Feather/Lucide-style icon paths (24×24 viewBox).
const ICON = {
  heart:
    "M 20.84 4.61 a 5.5 5.5 0 0 0 -7.78 0 L 12 5.67 l -1.06 -1.06 a 5.5 5.5 0 0 0 -7.78 7.78 l 1.06 1.06 L 12 21.23 l 7.78 -7.78 1.06 -1.06 a 5.5 5.5 0 0 0 0 -7.78 z",
  comment: "M 21 15 a 2 2 0 0 1 -2 2 H 7 l -4 4 V 5 a 2 2 0 0 1 2 -2 h 14 a 2 2 0 0 1 2 2 z",
  share: "M 22 2 L 11 13 M 22 2 l -7 20 -4 -9 -9 -4 20 -7 z",
  bookmark: "M 19 21 l -7 -5 -7 5 V 5 a 2 2 0 0 1 2 -2 h 10 a 2 2 0 0 1 2 2 z",
  music:
    "M 9 18 V 5 l 12 -2 v 13 M 9 18 a 3 3 0 1 1 -3 -3 3 3 0 0 1 3 3 z M 21 16 a 3 3 0 1 1 -3 -3 3 3 0 0 1 3 3 z",
  volume:
    "M 11 5 L 6 9 H 2 v 6 h 4 l 5 4 z M 19.07 4.93 a 10 10 0 0 1 0 14.14 M 15.54 8.46 a 5 5 0 0 1 0 7.07",
  plus: "M 12 5 v 14 M 5 12 h 14",
  home: "M 3 9 l 9 -7 9 7 v 11 a 2 2 0 0 1 -2 2 H 5 a 2 2 0 0 1 -2 -2 z",
  search: "M 11 19 a 8 8 0 1 0 0 -16 8 8 0 0 0 0 16 z M 21 21 l -4.35 -4.35",
  inbox:
    "M 22 12 h -6 l -2 3 h -4 l -2 -3 H 2 M 5.45 5.11 L 2 12 v 6 a 2 2 0 0 0 2 2 h 16 a 2 2 0 0 0 2 -2 v -6 l -3.45 -6.89 A 2 2 0 0 0 16.76 4 H 7.24 a 2 2 0 0 0 -1.79 1.11 z",
  user: "M 20 21 v -2 a 4 4 0 0 0 -4 -4 H 8 a 4 4 0 0 0 -4 4 v 2 M 12 11 a 4 4 0 1 0 0 -8 4 4 0 0 0 0 8 z",
};

const Icon: React.FC<{
  d: string;
  x: number;
  y: number;
  size: number;
  stroke: string;
  fill?: string;
  strokeWidth?: number;
}> = ({ d, x, y, size, stroke, fill = "none", strokeWidth = 2 }) => {
  // 24×24 viewBox → scale to `size`
  const s = size / 24;
  return (
    <g transform={`translate(${x - size / 2} ${y - size / 2}) scale(${s})`}>
      <path
        d={d}
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth / s}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
};

const SafeAreaOverlay: React.FC<{ width: number; height: number }> = ({ width, height }) => {
  const stroke = "rgba(255, 255, 255, 0.48)";
  const strokeSoft = "rgba(255, 255, 255, 0.24)";
  const strokeFaint = "rgba(255, 255, 255, 0.14)";
  const fontFamily = "-apple-system, system-ui, sans-serif";

  const safeW = width - SAFE_LEFT - SAFE_RIGHT;
  const safeH = height - SAFE_TOP - SAFE_BOTTOM;

  // Right action rail (heart / comment / share / bookmark / audio).
  const railX = width - 72;
  const railTop = height * 0.32;
  const railGap = 150;
  const iconSize = 56;
  const railItems: Array<{
    key: string;
    label: string;
    icon: string;
    filled: boolean;
  }> = [
    { key: "heart", label: "124K", icon: ICON.heart, filled: true },
    { key: "comment", label: "8.2K", icon: ICON.comment, filled: true },
    { key: "share", label: "1.3K", icon: ICON.share, filled: false },
    { key: "bookmark", label: "Save", icon: ICON.bookmark, filled: false },
  ];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.3 }}
    >
      {/* ── Safe area outline ───────────────────────────────────────── */}
      <rect
        x={SAFE_LEFT}
        y={SAFE_TOP}
        width={safeW}
        height={safeH}
        fill="none"
        stroke={strokeSoft}
        strokeWidth={2}
        strokeDasharray="14 10"
      />

      {/* ── Top-left: profile chip with follow button ──────────────── */}
      <g>
        {/* Avatar */}
        <circle
          cx={84}
          cy={SAFE_TOP - 110}
          r={40}
          fill={strokeFaint}
          stroke={stroke}
          strokeWidth={3}
        />
        <Icon d={ICON.user} x={84} y={SAFE_TOP - 110} size={48} stroke={stroke} strokeWidth={2.4} />
        {/* Username + subtitle */}
        <rect x={144} y={SAFE_TOP - 132} width={250} height={18} rx={9} fill={stroke} />
        <rect x={144} y={SAFE_TOP - 104} width={170} height={12} rx={6} fill={strokeSoft} />
        {/* Follow button */}
        <rect
          x={420}
          y={SAFE_TOP - 132}
          width={140}
          height={44}
          rx={22}
          fill="none"
          stroke={stroke}
          strokeWidth={3}
        />
        <rect x={448} y={SAFE_TOP - 117} width={84} height={14} rx={7} fill={stroke} />
      </g>

      {/* ── Top-right: mute / volume button ────────────────────────── */}
      <g>
        <circle
          cx={width - 72}
          cy={SAFE_TOP - 110}
          r={38}
          fill={strokeFaint}
          stroke={stroke}
          strokeWidth={3}
        />
        <Icon
          d={ICON.volume}
          x={width - 72}
          y={SAFE_TOP - 110}
          size={40}
          stroke={stroke}
          strokeWidth={2.4}
        />
      </g>

      {/* ── Right action rail ──────────────────────────────────────── */}
      {railItems.map((item, i) => {
        const cy = railTop + i * railGap;
        return (
          <g key={item.key}>
            <circle
              cx={railX}
              cy={cy}
              r={iconSize / 2 + 10}
              fill={strokeFaint}
              stroke={stroke}
              strokeWidth={2}
            />
            <Icon
              d={item.icon}
              x={railX}
              y={cy}
              size={iconSize}
              stroke={stroke}
              fill={item.filled ? strokeSoft : "none"}
              strokeWidth={2.4}
            />
            <text
              x={railX}
              y={cy + iconSize / 2 + 28}
              fill={stroke}
              fontSize={22}
              fontWeight={700}
              fontFamily={fontFamily}
              textAnchor="middle"
            >
              {item.label}
            </text>
          </g>
        );
      })}

      {/* ── Rotating audio disc at the bottom of the rail ──────────── */}
      <g>
        <circle
          cx={railX}
          cy={railTop + railItems.length * railGap + 30}
          r={iconSize / 2 + 4}
          fill={strokeFaint}
          stroke={stroke}
          strokeWidth={3}
        />
        <circle cx={railX} cy={railTop + railItems.length * railGap + 30} r={10} fill={stroke} />
      </g>

      {/* ── Bottom-left: @handle + caption + hashtags ──────────────── */}
      <g>
        {/* @handle */}
        <rect
          x={SAFE_LEFT}
          y={height - SAFE_BOTTOM + 46}
          width={safeW * 0.3}
          height={18}
          rx={9}
          fill={stroke}
        />
        {/* Caption line 1 */}
        <rect
          x={SAFE_LEFT}
          y={height - SAFE_BOTTOM + 84}
          width={safeW * 0.7}
          height={14}
          rx={7}
          fill={strokeSoft}
        />
        {/* Caption line 2 */}
        <rect
          x={SAFE_LEFT}
          y={height - SAFE_BOTTOM + 112}
          width={safeW * 0.5}
          height={14}
          rx={7}
          fill={strokeSoft}
        />
        {/* Hashtag pills */}
        <rect
          x={SAFE_LEFT}
          y={height - SAFE_BOTTOM + 144}
          width={140}
          height={22}
          rx={11}
          fill="none"
          stroke={strokeSoft}
          strokeWidth={2}
        />
        <rect
          x={SAFE_LEFT + 154}
          y={height - SAFE_BOTTOM + 144}
          width={116}
          height={22}
          rx={11}
          fill="none"
          stroke={strokeSoft}
          strokeWidth={2}
        />
        <rect
          x={SAFE_LEFT + 284}
          y={height - SAFE_BOTTOM + 144}
          width={92}
          height={22}
          rx={11}
          fill="none"
          stroke={strokeSoft}
          strokeWidth={2}
        />
      </g>

      {/* ── Audio track ticker (spinning disc glyph + track name) ── */}
      <g>
        <Icon
          d={ICON.music}
          x={SAFE_LEFT + 20}
          y={height - 130}
          size={28}
          stroke={stroke}
          strokeWidth={2.4}
        />
        <rect
          x={SAFE_LEFT + 44}
          y={height - 138}
          width={safeW * 0.55}
          height={12}
          rx={6}
          fill={strokeSoft}
        />
      </g>

      {/* ── Playback progress bar with thumb ───────────────────────── */}
      <g>
        <rect x={SAFE_LEFT} y={height - 90} width={safeW} height={4} rx={2} fill={strokeSoft} />
        <rect x={SAFE_LEFT} y={height - 90} width={safeW * 0.35} height={4} rx={2} fill={stroke} />
        <circle cx={SAFE_LEFT + safeW * 0.35} cy={height - 88} r={8} fill={stroke} />
      </g>

      {/* ── Bottom navigation bar (5 items) ────────────────────────── */}
      <g>
        {/* Faint separator line above the nav */}
        <line
          x1={0}
          y1={height - 66}
          x2={width}
          y2={height - 66}
          stroke={strokeFaint}
          strokeWidth={1}
        />
        {[
          { cx: width * 0.15, icon: ICON.home },
          { cx: width * 0.35, icon: ICON.search },
          { cx: width * 0.5, icon: null }, // + button
          { cx: width * 0.65, icon: ICON.inbox },
          { cx: width * 0.85, icon: ICON.user },
        ].map((item, i) => {
          const cy = height - 34;
          if (item.icon === null) {
            // Centered create button (big rounded rect with plus)
            return (
              <g key={i}>
                <rect
                  x={item.cx - 36}
                  y={cy - 18}
                  width={72}
                  height={36}
                  rx={10}
                  fill={strokeFaint}
                  stroke={stroke}
                  strokeWidth={2}
                />
                <Icon
                  d={ICON.plus}
                  x={item.cx}
                  y={cy}
                  size={22}
                  stroke={stroke}
                  strokeWidth={2.5}
                />
              </g>
            );
          }
          return (
            <Icon
              key={i}
              d={item.icon}
              x={item.cx}
              y={cy}
              size={28}
              stroke={stroke}
              strokeWidth={2.4}
            />
          );
        })}
      </g>
    </svg>
  );
};
